import { RETIRED_AGENT_REFERRAL_CODES } from '~/constants/referral';

export const AGENT_REFERRAL_COOKIE_KEY = 'agent_referral_code';
// Server-side twin written by server/middleware/capture-referral.ts and read by
// the signup endpoints as a fallback (server/utils/auth/applyReferralCode.ts).
export const SERVER_REFERRAL_COOKIE_KEY = 'dic_ref';
export const AGENT_REFERRAL_COOKIE_MAX_AGE = 60 * 60 * 24 * 30; // 30 days
export const AGENT_REFERRAL_SOURCE_PREFIX = 'agent-ref:';

const REFERRAL_CODE_INVALID_CHARS = /[^A-Z0-9-]/g;

// Personal links (/a/<handle>) are for buyers, never agent recruitment — no referral capture.
export const isPersonalLinkPath = (path: string): boolean => /^\/a(?:[/?#]|$)/.test(path);

export const normalizeAgentReferralCode = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;

  const normalized = value.trim().toUpperCase().replace(REFERRAL_CODE_INVALID_CHARS, '');
  return normalized || null;
};

export const normalizeAgentReferralParam = (value: string | string[] | null | undefined): string | null => {
  if (Array.isArray(value)) {
    return normalizeAgentReferralCode(value[0]);
  }
  return normalizeAgentReferralCode(value);
};

type ReferralCodeOwner = { agentReference: string; referralCode?: string | null };

// The code that credits this agent as referrer: a generated referralCode replaces the agentReference,
// and a retired agentReference with no replacement has none (the agent generates a new one).
export const currentAgentReferralCode = (profile: ReferralCodeOwner): string | null => {
  if (profile.referralCode) return profile.referralCode;
  return RETIRED_AGENT_REFERRAL_CODES.includes(profile.agentReference) ? null : profile.agentReference;
};

export const referralCodeCredits = (profile: ReferralCodeOwner, code: string): boolean =>
  currentAgentReferralCode(profile) === code;

export const buildAgentReferralSource = (referralCode: string): string => {
  return `${AGENT_REFERRAL_SOURCE_PREFIX}${referralCode}`;
};

export const parseAgentReferralSource = (registerSource: string | null | undefined): string | null => {
  if (!registerSource) return null;
  if (!registerSource.startsWith(AGENT_REFERRAL_SOURCE_PREFIX)) return null;
  return normalizeAgentReferralCode(registerSource.slice(AGENT_REFERRAL_SOURCE_PREFIX.length));
};

// An admin changing or clearing the referrer: a referral signup source follows the new referrer
// (or goes, when cleared), since referral lists also count agents by it. Any other source stays.
export const registerSourceAfterReferrerChange = (
  registerSource: string | null | undefined,
  nextReferralCode: string | null,
): string | null => {
  if (!parseAgentReferralSource(registerSource)) return registerSource ?? null;
  return nextReferralCode ? buildAgentReferralSource(nextReferralCode) : null;
};

type AgentReferralTrackingInput = {
  referralCode: string;
  referredByAgentProfileId: string;
  referredByUserId: string | null;
};

export const withAgentReferralTrackingInfo = (
  currentTrackingInfo: unknown,
  referral: AgentReferralTrackingInput,
): Record<string, unknown> => {
  const baseTrackingInfo =
    currentTrackingInfo && typeof currentTrackingInfo === 'object' && !Array.isArray(currentTrackingInfo)
      ? (currentTrackingInfo as Record<string, unknown>)
      : {};

  return {
    ...baseTrackingInfo,
    agentReferral: {
      referralCode: referral.referralCode,
      referredByAgentProfileId: referral.referredByAgentProfileId,
      referredByUserId: referral.referredByUserId,
      attributedAt: new Date().toISOString(),
    },
  };
};
